import { FiChevronRight } from "react-icons/fi";
import { LuEye, LuPencil } from "react-icons/lu";
import {
  RelatedRecord,
  useRelatedRecordsTab,
} from "../hooks/useRelatedRecordsTab";

type ChildRecordType = "CHLD" | "ENRL" | "CLEN" | "FAMI" | "LANG" | "SLNG";
type StaffRecordType = "STFF" | "SASS" | "SCLR";
type RecordType = ChildRecordType | StaffRecordType | "CLRM" | "ATTN";

type RelatedRecordsTabProps = {
  entityId: string | undefined;
  currentRecordType: RecordType | string;
  csppid: string | undefined;
  secondaryRecordType?: string;
  onEditRecord?: (recordType: string, recordData: any) => void;
  onDetailViewChange?: (isDetailView: boolean, onBack?: () => void) => void;
  viewPermissions?: Record<string, boolean>;
  editPermissions?: Record<string, boolean>;
};

const NAVY_COLOR = "#0F2A4A";

const RecordBadge = ({ type }: { type: "Primary" | "Secondary" }) => {
  const isPrimary = type === "Primary";
  return (
    <span
      style={{
        backgroundColor: isPrimary ? NAVY_COLOR : "white",
        color: isPrimary ? "white" : "#374151",
        border: isPrimary ? "none" : "1px solid #D1D5DB",
        fontSize: "11px",
        fontWeight: 500,
        padding: "2px 8px",
        borderRadius: "4px",
        marginLeft: "8px",
      }}
    >
      {type}
    </span>
  );
};

const TableHeader = ({ children }: { children: React.ReactNode }) => (
  <th
    style={{
      padding: "16px 24px",
      fontWeight: 600,
      color: "white",
      backgroundColor: "#475569",
      borderBottom: "none",
    }}
  >
    {children}
  </th>
);

const EditButton = ({
  onClick,
  canEdit = true,
}: {
  onClick: () => void;
  canEdit?: boolean;
}) => (
  <button
    type="button"
    className="btn btn-sm d-inline-flex align-items-center gap-2"
    style={{
      borderRadius: "6px",
      padding: "6px 12px",
      border: `1px solid ${NAVY_COLOR}`,
      color: NAVY_COLOR,
      backgroundColor: "white",
    }}
    onClick={onClick}
  >
    {canEdit ? <LuPencil size={14} /> : <LuEye size={14} />}
    <span>{canEdit ? "Edit" : "View"}</span>
  </button>
);

const ViewRecordsButton = ({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) => (
  <button
    type="button"
    className="btn btn-sm d-inline-flex align-items-center gap-2"
    style={{
      borderRadius: "6px",
      padding: "6px 12px",
      border: `1px solid ${NAVY_COLOR}`,
      color: NAVY_COLOR,
      backgroundColor: "white",
    }}
    onClick={onClick}
  >
    <span>View {count} records</span>
    <FiChevronRight size={14} />
  </button>
);

const EmptyState = () => (
  <div
    className="overflow-hidden"
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: "0.375rem 0.375rem 0px 0px",
    }}
  >
    <table className="table table-striped mb-0">
      <thead>
        <tr>
          <TableHeader>Record Type</TableHeader>
          <TableHeader>Status</TableHeader>
          <th
            style={{
              padding: "16px 24px",
              fontWeight: 600,
              textAlign: "right",
              color: "white",
              backgroundColor: "#475569",
              borderBottom: "none",
            }}
          >
            Actions
          </th>
        </tr>
      </thead>
    </table>
    <div className="p-4 text-center text-muted">
      No related records available for this record type.
    </div>
  </div>
);

const DetailView = ({
  selectedRecordType,
  selectedRecords,
  isStaffContext,
  entityId,
  csppid,
  formatRecordId,
  onEditRecord,
  canView = true,
  canEdit = true,
}: {
  selectedRecordType: string;
  selectedRecords: any[];
  isStaffContext: boolean;
  entityId: string | undefined;
  csppid: string | undefined;
  formatRecordId: (recordType: string, index: number) => string;
  onEditRecord?: (recordType: string, recordData: any) => void;
  canView?: boolean;
  canEdit?: boolean;
}) => {
  if (!canView) {
    return (
      <div
        className="d-flex align-items-center justify-content-center p-4"
        style={{
          backgroundColor: "#F8FAFC",
          border: "1px solid #E5E7EB",
          borderRadius: "0.375rem 0.375rem 0px 0px",
          minHeight: "120px",
        }}
      >
        <div className="d-flex align-items-center gap-2 text-muted">
          <LuEye size={16} />
          <span>No access for {selectedRecordType} records</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden"
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "0.375rem 0.375rem 0px 0px",
      }}
    >
      <table className="table table-striped mb-0">
        <thead>
          <tr>
            <TableHeader>Record ID</TableHeader>
            <TableHeader>Program Year</TableHeader>
            <TableHeader>School</TableHeader>
            <TableHeader>
              {isStaffContext ? "Staff Local ID" : "CSPPID"}
            </TableHeader>
            <th
              style={{
                padding: "16px 24px",
                fontWeight: 600,
                textAlign: "right",
                color: "white",
                backgroundColor: "#475569",
                borderBottom: "none",
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {selectedRecords.map((record, index) => (
            <tr key={record.Id ?? record.RecordId ?? index}>
              <td
                style={{
                  padding: "16px 24px",
                  fontWeight: 500,
                  verticalAlign: "middle",
                }}
              >
                {formatRecordId(selectedRecordType, index)}
              </td>
              <td style={{ padding: "16px 24px", verticalAlign: "middle" }}>
                {record.ProgramYear || "—"}
              </td>
              <td style={{ padding: "16px 24px", verticalAlign: "middle" }}>
                {record.PreschoolCode || "—"}
              </td>
              <td style={{ padding: "16px 24px", verticalAlign: "middle" }}>
                {isStaffContext ? entityId : csppid || "—"}
              </td>
              <td
                style={{
                  padding: "16px 24px",
                  textAlign: "right",
                  verticalAlign: "middle",
                }}
              >
                <EditButton
                  onClick={() => onEditRecord?.(selectedRecordType, record)}
                  canEdit={canEdit}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SummaryView = ({
  relatedRecords,
  onEditRecord,
  handleViewRecords,
  viewPermissions = {},
  editPermissions = {},
}: {
  relatedRecords: RelatedRecord[];
  onEditRecord?: (recordType: string, recordData: any) => void;
  handleViewRecords: (recordType: string, records: any[]) => void;
  viewPermissions?: Record<string, boolean>;
  editPermissions?: Record<string, boolean>;
}) => {
  const canViewRecord = (recordType: string) =>
    viewPermissions[recordType] ?? true;
  const canEditRecord = (recordType: string) =>
    editPermissions[recordType] ?? true;

  return (
    <div
      className="overflow-hidden"
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "0.375rem 0.375rem 0px 0px",
      }}
    >
      <table
        className="table table-striped mb-0"
        style={{ tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: "16px 24px",
                fontWeight: 600,
                width: "30%",
                color: "white",
                backgroundColor: "#475569",
                borderBottom: "none",
              }}
            >
              Record Type
            </th>
            <th
              style={{
                padding: "16px 24px",
                fontWeight: 600,
                width: "45%",
                color: "white",
                backgroundColor: "#475569",
                borderBottom: "none",
              }}
            >
              Status
            </th>
            <th
              style={{
                padding: "16px 24px",
                fontWeight: 600,
                width: "25%",
                textAlign: "right",
                color: "white",
                backgroundColor: "#475569",
                borderBottom: "none",
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {relatedRecords.map((record) => {
            const canView = canViewRecord(record.recordType);
            const canEdit = canEditRecord(record.recordType);
            return (
              <tr key={record.recordType}>
                <td
                  style={{
                    padding: "16px 24px",
                    fontWeight: 500,
                    verticalAlign: "middle",
                  }}
                >
                  {record.recordType}
                  {record.status === "Primary" && (
                    <RecordBadge type="Primary" />
                  )}
                  {record.status === "Secondary" && (
                    <RecordBadge type="Secondary" />
                  )}
                </td>
                <td
                  style={{
                    padding: "16px 24px",
                    verticalAlign: "middle",
                    color: !canView
                      ? "#9CA3AF"
                      : record.status === "Missing"
                        ? "#9CA3AF"
                        : "#000000",
                    fontStyle:
                      !canView || record.status === "Missing"
                        ? "italic"
                        : "normal",
                  }}
                >
                  {!canView ? "No access" : record.recordId}
                </td>
                <td
                  style={{
                    padding: "16px 24px",
                    textAlign: "right",
                    verticalAlign: "middle",
                  }}
                >
                  {canView && record.count === 1 && (
                    <EditButton
                      onClick={() =>
                        onEditRecord?.(record.recordType, record.records[0])
                      }
                      canEdit={canEdit}
                    />
                  )}
                  {canView && record.count > 1 && (
                    <ViewRecordsButton
                      count={record.count}
                      onClick={() =>
                        handleViewRecords(record.recordType, record.records)
                      }
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export function RelatedRecordsTab({
  entityId,
  currentRecordType,
  csppid,
  secondaryRecordType,
  onEditRecord,
  onDetailViewChange,
  viewPermissions = {},
  editPermissions = {},
}: RelatedRecordsTabProps) {
  const {
    viewMode,
    selectedRecordType,
    selectedRecords,
    relatedRecords,
    isStaffContext,
    handleViewRecords,
    formatRecordId,
  } = useRelatedRecordsTab({
    entityId,
    currentRecordType,
    csppid,
    secondaryRecordType,
    onDetailViewChange,
  });

  const canViewRecord = (recordType: string) =>
    viewPermissions[recordType] ?? true;
  const canEditRecord = (recordType: string) =>
    editPermissions[recordType] ?? true;

  if (relatedRecords.length === 0) {
    return <EmptyState />;
  }

  if (viewMode === "detail" && selectedRecordType) {
    const canView = canViewRecord(selectedRecordType);
    const canEdit = canEditRecord(selectedRecordType);
    return (
      <DetailView
        selectedRecordType={selectedRecordType}
        selectedRecords={selectedRecords}
        isStaffContext={isStaffContext}
        entityId={entityId}
        csppid={csppid}
        formatRecordId={formatRecordId}
        onEditRecord={canView ? onEditRecord : undefined}
        canView={canView}
        canEdit={canEdit}
      />
    );
  }

  return (
    <SummaryView
      relatedRecords={relatedRecords}
      onEditRecord={onEditRecord}
      handleViewRecords={handleViewRecords}
      viewPermissions={viewPermissions}
      editPermissions={editPermissions}
    />
  );
}
